import DeleteIcon from '@mui/icons-material/Delete'
import RestoreIcon from '@mui/icons-material/Restore'
import SearchIcon from '@mui/icons-material/Search'
import { Box, Breadcrumbs, Stack, TextField, Typography } from '@mui/material'
import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { LuX } from 'react-icons/lu'
import { useDispatch } from 'react-redux'
import { useLocation, useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'

import Button from '@/components/Button'
import Dialog from '@/components/Dialog'
import Icon from '@/components/Icon'
import IconText from '@/components/IconText'
import { useAppSelector } from '@/hooks/redux'
import {
  useDeleteCaseMutation,
  useRestoreImageMutation,
} from '@/store/apiService'
import { clearTrashSelectedCases } from '@/store/project'

interface TrashHeaderProps {
  selectedRows: number[]
  refetch: () => void
  search: string
  setSearch: (val: string) => void
}

const TrashHeader = ({
  selectedRows,
  refetch,
  search,
  setSearch,
}: TrashHeaderProps) => {
  const { t } = useTranslation('translation', {
    keyPrefix: 'TrashProject',
  })

  const dispatch = useDispatch()
  const navigate = useNavigate()
  const location = useLocation()
  const urlCaseId = new URLSearchParams(location.search).get('caseId') || ''
  const [deleteCase, { isLoading: isDeletingCase }] = useDeleteCaseMutation()
  const [restoreCase, { isLoading: isRestoringCase }] =
    useRestoreImageMutation()
  const caseIds = useAppSelector((state) => state.project.selectedTrashIds)

  const [showInfoBox, setShowInfoBox] = useState(true)
  const [isDeleteDialog, setIsDeleteDialog] = useState(false)

  const deleteCases = async () => {
    if (caseIds) {
      try {
        await deleteCase({ ids: [caseIds], hard_delete: true }).unwrap()
        setIsDeleteDialog(false)
        toast.success(t('TrashActionsDialog.deleteSuccess'))
        dispatch(clearTrashSelectedCases())
        refetch()
      } catch (err) {
        toast.error(t('TrashActionsDialog.deleteError'))
      }
    }
  }

  const restoreCases = async () => {
    if (caseIds) {
      try {
        await restoreCase({ ids: [caseIds] }).unwrap()
        toast.success(t('TrashActions.projectRestored'))
        dispatch(clearTrashSelectedCases())
        refetch()
      } catch (err) {
        toast.error(t('TrashActions.projectRestoreFailed'))
      }
    }
  }

  useEffect(() => {
    if (selectedRows.length > 0) {
      setShowInfoBox(true)
    }
  }, [selectedRows])

  return (
    <>
      <Box display={'flex'} flexDirection={'column'} gap={4}>
        <Box display="flex" flexDirection={'column'} gap={1}>
          <Box>
            <Breadcrumbs sx={{ color: '#C6C6C8' }}>
              <Typography
                color="#C6C6C8"
                fontSize={'16px'}
                fontWeight={400}
                lineHeight={'24px'}
                onClick={() => {
                  if (urlCaseId) {
                    navigate(`/projects?caseId=${urlCaseId}`)
                  } else {
                    navigate('/projects')
                  }
                }}
                sx={{ cursor: 'pointer' }}
              >
                {t('projects')}
              </Typography>
              <Typography
                color="#C6C6C8"
                fontSize={'16px'}
                fontWeight={400}
                lineHeight={'24px'}
              >
                {t('trash')}
              </Typography>
            </Breadcrumbs>
          </Box>
          <Typography fontSize={'32px'} fontWeight={700} color="white">
            {t('trash')}
          </Typography>
        </Box>

        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          spacing={2}
          paddingBottom={3}
        >
          {showInfoBox ? (
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                bgcolor: '#464646',
                borderRadius: '4px',
                px: 2,
                py: 1,
                flexGrow: 1,
              }}
            >
              {selectedRows.length > 0 ? (
                <Stack direction="row" spacing={2} alignItems="center">
                  <Typography
                    sx={{ color: 'white', fontSize: '14px', fontWeight: 400 }}
                  >
                    {selectedRows.length} {t('select')}:
                  </Typography>
                  <IconText
                    icon={<RestoreIcon />}
                    label={t('TrashActions.restore').toUpperCase()}
                    onClick={restoreCases}
                  />
                  <IconText
                    icon={<DeleteIcon />}
                    label={t('TrashActions.delete').toUpperCase()}
                    onClick={() => {
                      setIsDeleteDialog(true)
                    }}
                  />
                </Stack>
              ) : (
                <Typography
                  fontSize={'14px'}
                  fontWeight={400}
                  lineHeight={'20.02px'}
                >
                  {t('itemInTrash')}
                </Typography>
              )}
              <Icon
                icon={LuX}
                size={18}
                onClick={() => {
                  if (selectedRows.length > 0) {
                    dispatch(clearTrashSelectedCases())
                  } else {
                    setShowInfoBox(false)
                  }
                }}
              />
            </Box>
          ) : (
            <Box sx={{ flexGrow: 1 }} />
          )}

          <TextField
            size="small"
            placeholder="Search"
            variant="outlined"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            InputProps={{
              endAdornment: <SearchIcon fontSize="small" sx={{ mr: 1 }} />,
            }}
            sx={searchFieldStyle}
          />
        </Stack>
      </Box>
      <Dialog
        triggerElement={null}
        isOpen={isDeleteDialog}
        onClose={() => {
          setIsDeleteDialog(false)
        }}
        title={t('TrashActionsDialog.title')}
        content={<p>{t('TrashActionsDialog.description')}</p>}
        paperStyles={{ width: '600px' }}
        actionButtons={
          <Box display="flex" justifyContent="flex-end" gap={2}>
            <Button
              variant="outlined"
              label={t('TrashActionsDialog.cancelButton')}
              onClick={() => {
                setIsDeleteDialog(false)
              }}
              textTransform="uppercase"
            />
            <Button
              variant="contained"
              label={t('TrashActionsDialog.deleteButton')}
              onClick={deleteCases}
              textTransform="uppercase"
              loading={isDeletingCase}
              disabled={isDeletingCase}
            />
          </Box>
        }
      />
    </>
  )
}

const searchFieldStyle = {
  input: { color: 'white' },
  '& .MuiOutlinedInput-root': {
    backgroundColor: 'rgba(255,255,255,0.05)',
    '&:hover .MuiOutlinedInput-notchedOutline': {
      borderColor: '#454545',
    },
    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
      borderColor: '#90CAF9',
    },
    '& input': {
      '&:-webkit-autofill': {
        boxShadow: '0 0 0 1000px #292929 inset',
        transition: 'background-color 5000s ease-in-out 0s',
      },
    },
  },
}

export default TrashHeader
